/**
 * SwarmConsole — Phase G.1
 *
 * Single owner-side view for the autonomous 9-agent swarm.
 * Sections:
 *   1. Top KPIs + last cycle summary
 *   2. Agent status grid (9 cards: ok / under_quota / asleep / disabled / error)
 *   3. Tabbed lower section: Content Bank · Lead Drafts · Audiences · Estimates · Cycles
 *   4. "Force run" button per agent + global "Run cycle now"
 *   5. Settings (cadence, BH window, per-agent enable toggles)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  Brain,
  CalendarCheck,
  CheckCircle2,
  Clock,
  Compass,
  Copy,
  FileText,
  Hourglass,
  Inbox,
  Loader2,
  Megaphone,
  Pause,
  Play,
  PlayCircle,
  RefreshCw,
  Settings as SettingsIcon,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Star,
  Target,
  Users,
  Wrench,
  XCircle,
  Zap,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { crmApi } from "@/lib/ownerClient";

const AGENT_ICONS = {
  content_production: Megaphone,
  audience_building: Target,
  lead_research: Brain,
  subcontractor_pipeline: Users,
  investor_content: Sparkles,
  hiring_content: Inbox,
  nurture_followup: Hourglass,
  maintenance_lead_gen: Wrench,
  lead_outreach: PlayCircle,
  client_response: FileText,
  estimating: ShieldCheck,
  retention_reviews: Star,
};

const STATUS_TONES = {
  ok: { dot: "bg-emerald-400", text: "text-emerald-200", border: "border-emerald-500/40" },
  under_quota: { dot: "bg-amber-400", text: "text-amber-200", border: "border-amber-500/40" },
  asleep: { dot: "bg-zinc-500", text: "text-zinc-400", border: "border-zinc-700" },
  disabled: { dot: "bg-zinc-600", text: "text-zinc-500", border: "border-zinc-800" },
  error: { dot: "bg-red-400", text: "text-red-200", border: "border-red-500/40" },
};

function relTime(ts) {
  if (!ts) return "—";
  const dt = new Date(ts);
  const now = new Date();
  const sec = Math.round((now - dt) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return dt.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function Kpi({ icon: Icon, label, value, hint, tone = "default", testId }) {
  const toneCls =
    tone === "pass"
      ? "text-emerald-300"
      : tone === "warn"
      ? "text-amber-300"
      : tone === "fail"
      ? "text-red-300"
      : "text-white";
  return (
    <Card className="bg-zinc-950 border-zinc-800" data-testid={testId}>
      <CardContent className="p-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-zinc-500">
          <Icon className="h-3 w-3" />
          {label}
        </div>
        <div className={`text-2xl font-semibold mt-1 ${toneCls}`}>{value}</div>
        {hint ? <div className="text-[10.5px] text-zinc-500 mt-0.5">{hint}</div> : null}
      </CardContent>
    </Card>
  );
}

function AgentCard({ spec, lastRecord, busy, onForceRun }) {
  const Icon = AGENT_ICONS[spec.slug] || Sparkles;
  const status = lastRecord?.status || "asleep";
  const tone = STATUS_TONES[status] || STATUS_TONES.asleep;
  const tierBadge = spec.tier === "frontend" ? "BH" : "24/7";
  const personaInitials = spec.persona_initials || "";
  const personaName = spec.persona_name || "";
  const personaRole = spec.persona_role || "";
  return (
    <Card
      className={`bg-zinc-950 ${tone.border} border`}
      data-testid={`swarm-agent-card-${spec.slug}`}
    >
      <CardContent className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2 min-w-0">
            <div className={`rounded-md p-1.5 border ${tone.border} bg-black mt-0.5`}>
              <Icon className={`h-4 w-4 ${tone.text}`} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-white font-semibold text-[13px] truncate">{spec.name}</span>
                <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-[9.5px] py-0">
                  {tierBadge}
                </Badge>
              </div>
              {personaName ? (
                <div
                  className="flex items-center gap-1.5 mt-1"
                  data-testid={`swarm-agent-persona-${spec.slug}`}
                >
                  <span
                    className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 text-[8.5px] font-semibold tracking-tight"
                    title={`${personaName} — ${personaRole}`}
                  >
                    {personaInitials}
                  </span>
                  <span className="text-[10.5px] text-zinc-400 truncate">
                    <span className="text-zinc-300">{personaName}</span>
                    {personaRole ? <span className="text-zinc-600"> · {personaRole}</span> : null}
                  </span>
                </div>
              ) : null}
              <div className="text-[10.5px] text-zinc-500 mt-0.5">
                min {spec.min_output}/cycle
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <span className={`h-2 w-2 rounded-full ${tone.dot}`} />
          </div>
        </div>
        <div className="mt-2 text-[11.5px] text-zinc-400 line-clamp-2">
          {lastRecord?.summary || spec.description}
        </div>
        {spec.slug === "lead_outreach" && lastRecord?.details ? (
          <div
            className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[10px]"
            data-testid={`swarm-dana-stats-${spec.slug}`}
          >
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-violet-500/10 border border-violet-500/30 text-violet-200"
              title="Dana CAN-SPAM gate outcomes this cycle"
            >
              <ShieldAlert className="h-2.5 w-2.5" />
              <span className="font-semibold tracking-tight">DW</span>
            </span>
            <span className="text-emerald-300">
              ✓ {lastRecord.details.dana_passed ?? 0}
            </span>
            <span className="text-red-300">
              ✗ {lastRecord.details.dana_blocked ?? 0}
            </span>
            {lastRecord.details.dana_warnings ? (
              <span className="text-amber-300">
                ⚠ {lastRecord.details.dana_warnings}
              </span>
            ) : null}
            {lastRecord.details.auto_send === false ? (
              <span className="text-zinc-500">· draft-only</span>
            ) : null}
          </div>
        ) : null}
        <div className="mt-2 flex items-center justify-between text-[10.5px]">
          <span className={tone.text}>
            {status.replace(/_/g, " ")} · last output {lastRecord?.output_count ?? "—"}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onForceRun(spec.slug)}
            disabled={busy}
            className="h-7 px-2 border-zinc-800 text-zinc-200 hover:bg-zinc-900"
            data-testid={`swarm-force-run-${spec.slug}`}
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
          </Button>
        </div>
        {lastRecord?.error ? (
          <div className="mt-1.5 text-[10.5px] text-red-300 line-clamp-2">{lastRecord.error}</div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function CopyableBlock({ text, label, ariaLabel }) {
  function copy() {
    if (!navigator.clipboard) {
      toast.error("Clipboard unavailable");
      return;
    }
    navigator.clipboard.writeText(text || "").then(
      () => toast.success(`${label || "Content"} copied`),
      () => toast.error("Copy failed"),
    );
  }
  return (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      onClick={copy}
      className="h-7 px-2 text-zinc-300 hover:text-white"
      aria-label={ariaLabel || "Copy"}
    >
      <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
    </Button>
  );
}

function ContentBankTab({ items, onMark }) {
  const grouped = useMemo(() => {
    const out = {};
    for (const it of items) {
      const cat = it.category || "uncategorized";
      if (!out[cat]) out[cat] = [];
      out[cat].push(it);
    }
    return out;
  }, [items]);
  if (items.length === 0) {
    return (
      <div className="text-center py-10 text-zinc-500">
        <Megaphone className="h-6 w-6 text-amber-400 mx-auto mb-2" />
        <div>No content yet. Force-run Content Production to start the bank.</div>
      </div>
    );
  }
  return (
    <div className="space-y-5">
      {Object.entries(grouped).map(([cat, list]) => (
        <div key={cat}>
          <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-2">
            {cat} · {list.length}
          </div>
          <div className="grid gap-2">
            {list.map((it) => {
              const fullText = [
                it.headline,
                "",
                it.body,
                "",
                (it.services_listed || []).map((s) => `• ${s}`).join("\n"),
                "",
                it.cta,
                (it.hashtags || []).map((h) => `#${h.replace(/^#/, "")}`).join(" "),
              ]
                .filter(Boolean)
                .join("\n");
              return (
                <Card key={it.id} className="bg-black border-zinc-800">
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] text-white font-semibold leading-snug">
                          {it.headline}
                        </div>
                        <div className="text-[12.5px] text-zinc-300 mt-1 whitespace-pre-wrap line-clamp-6">
                          {it.body}
                        </div>
                        {(it.services_listed || []).length > 0 ? (
                          <div className="text-[11px] text-zinc-500 mt-2">
                            services: {it.services_listed.slice(0, 4).join(" · ")}
                          </div>
                        ) : null}
                        {it.cta ? (
                          <div className="text-[11.5px] text-amber-300 mt-1.5 italic">→ {it.cta}</div>
                        ) : null}
                        {it.posting_groups_hint ? (
                          <div className="text-[10.5px] text-zinc-500 mt-1">
                            target: {it.posting_groups_hint}
                          </div>
                        ) : null}
                        {it.compliance_violations && it.compliance_violations.length > 0 ? (
                          <div className="text-[10.5px] text-red-300 mt-1">
                            ⚠ compliance: {it.compliance_violations.join("; ")}
                          </div>
                        ) : null}
                      </div>
                      <div className="flex flex-col gap-1 items-end shrink-0">
                        <Badge
                          variant="outline"
                          className={
                            it.status === "posted"
                              ? "border-emerald-500/40 text-emerald-200 text-[9.5px]"
                              : it.status === "compliance_review"
                              ? "border-red-500/40 text-red-200 text-[9.5px]"
                              : "border-amber-500/40 text-amber-200 text-[9.5px]"
                          }
                        >
                          {it.status}
                        </Badge>
                        <CopyableBlock text={fullText} label="Post" />
                        {it.status !== "posted" ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => onMark(it.id, "posted")}
                            className="h-7 text-[10.5px] text-emerald-200 hover:bg-emerald-500/10"
                            data-testid={`mark-posted-${it.id}`}
                          >
                            Mark posted
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function LeadDraftsTab({ items, onUpdate }) {
  const byTier = { 1: [], 2: [], 3: [] };
  for (const d of items) byTier[d.tier || 3].push(d);
  if (items.length === 0) {
    return (
      <div className="text-center py-10 text-zinc-500">
        <Brain className="h-6 w-6 text-amber-400 mx-auto mb-2" />
        <div>No lead drafts yet. Lead Research drafts will appear here.</div>
      </div>
    );
  }
  return (
    <div className="space-y-5">
      {[1, 2, 3].map((tier) => (
        <div key={tier}>
          <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-2 flex items-center gap-2">
            Tier {tier} · {byTier[tier].length}
            <Badge
              variant="outline"
              className={
                tier === 1
                  ? "border-red-500/40 text-red-200 text-[9.5px]"
                  : tier === 2
                  ? "border-amber-500/40 text-amber-200 text-[9.5px]"
                  : "border-zinc-700 text-zinc-400 text-[9.5px]"
              }
            >
              {tier === 1 ? "Investors / commercial / multi-prop" : tier === 2 ? "Homeowners w/ project" : "Early interest"}
            </Badge>
          </div>
          <div className="grid gap-2">
            {byTier[tier].map((d) => {
              const fullMsg = [d.subject && `Subject: ${d.subject}`, "", d.message].filter(Boolean).join("\n");
              return (
                <Card key={d.id} className="bg-black border-zinc-800">
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[13px] text-white font-semibold truncate">{d.label}</span>
                          <Badge variant="outline" className="border-zinc-700 text-zinc-300 text-[9.5px]">
                            {d.channel}
                          </Badge>
                          <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-[9.5px]">
                            {d.status}
                          </Badge>
                          {d.audience ? (
                            <Badge variant="outline" className="border-zinc-800 text-zinc-500 text-[9.5px]">
                              {d.audience}
                            </Badge>
                          ) : null}
                        </div>
                        {d.tier_reasoning ? (
                          <div className="text-[10.5px] text-zinc-500 mt-1">{d.tier_reasoning}</div>
                        ) : null}
                        {d.subject ? (
                          <div className="text-[12px] text-amber-300 mt-1.5">📨 {d.subject}</div>
                        ) : null}
                        <div className="text-[12.5px] text-zinc-300 mt-1.5 whitespace-pre-wrap line-clamp-6">
                          {d.message}
                        </div>
                        {Array.isArray(d.flags) && d.flags.length > 0 ? (
                          <div className="text-[10.5px] text-red-300 mt-1">
                            ⚠ {d.flags.slice(0, 3).join(" · ")}
                          </div>
                        ) : null}
                      </div>
                      <div className="flex flex-col gap-1 items-end shrink-0">
                        <CopyableBlock text={fullMsg} label="Draft" />
                        {d.status === "ready" ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onUpdate(d.id, { status: "sent" })}
                            className="h-7 text-[10.5px] text-emerald-200 hover:bg-emerald-500/10"
                            data-testid={`mark-sent-${d.id}`}
                          >
                            Mark sent
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function AudienceTab({ items, onUpdate }) {
  if (items.length === 0) {
    return (
      <div className="text-center py-10 text-zinc-500">
        <Target className="h-6 w-6 text-amber-400 mx-auto mb-2" />
        <div>No audience list yet. Audience Building will populate this.</div>
      </div>
    );
  }
  return (
    <div className="grid gap-2">
      {items.map((g) => (
        <Card key={g.id} className="bg-black border-zinc-800">
          <CardContent className="p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[13px] text-white font-semibold">{g.name}</span>
                  <Badge variant="outline" className="border-zinc-700 text-zinc-300 text-[9.5px]">
                    {g.platform}
                  </Badge>
                  <Badge
                    variant="outline"
                    className={
                      g.relevance === "high"
                        ? "border-emerald-500/40 text-emerald-200 text-[9.5px]"
                        : g.relevance === "medium"
                        ? "border-amber-500/40 text-amber-200 text-[9.5px]"
                        : "border-zinc-700 text-zinc-400 text-[9.5px]"
                    }
                  >
                    {g.relevance}
                  </Badge>
                  <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-[9.5px]">
                    {g.estimated_members?.toLocaleString?.() || g.estimated_members || 0} members
                  </Badge>
                  <Badge variant="outline" className="border-zinc-800 text-zinc-500 text-[9.5px]">
                    {g.category}
                  </Badge>
                </div>
                {g.why_relevant ? (
                  <div className="text-[12px] text-zinc-300 mt-1.5">{g.why_relevant}</div>
                ) : null}
                {g.url_hint ? (
                  <div className="text-[11px] text-zinc-500 mt-1 truncate">
                    🔗 {g.url_hint}
                  </div>
                ) : null}
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <Badge variant="outline" className="border-zinc-700 text-zinc-300 text-[9.5px]">
                  {g.status}
                </Badge>
                {g.status !== "active" ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onUpdate(g.id, { status: "active" })}
                    className="h-7 text-[10.5px] text-emerald-200 hover:bg-emerald-500/10"
                  >
                    Mark active
                  </Button>
                ) : null}
                {g.status !== "rejected" ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onUpdate(g.id, { status: "rejected" })}
                    className="h-7 text-[10.5px] text-zinc-400 hover:bg-zinc-800"
                  >
                    Reject
                  </Button>
                ) : null}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function EstimatesTab({ items }) {
  if (items.length === 0) {
    return (
      <div className="text-center py-10 text-zinc-500">
        <ShieldCheck className="h-6 w-6 text-amber-400 mx-auto mb-2" />
        <div>No estimates yet. Estimating agent activates during business hours.</div>
      </div>
    );
  }
  return (
    <div className="grid gap-2">
      {items.map((est) => (
        <Card key={est.id} className="bg-black border-zinc-800">
          <CardContent className="p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[13px] text-white font-semibold">{est.project_title || est.label}</span>
                  <Badge
                    variant="outline"
                    className={
                      est.status === "owner_review"
                        ? "border-red-500/40 text-red-200 text-[9.5px]"
                        : "border-emerald-500/40 text-emerald-200 text-[9.5px]"
                    }
                  >
                    {est.status}
                  </Badge>
                  <Badge variant="outline" className="border-amber-500/40 text-amber-200 text-[10px]">
                    ${est.total_usd?.toLocaleString() || 0}
                  </Badge>
                </div>
                <div className="text-[12px] text-zinc-300 mt-1.5 whitespace-pre-wrap line-clamp-4">
                  {est.scope_of_work}
                </div>
                <div className="text-[10.5px] text-zinc-500 mt-1.5">
                  Labor ${est.labor?.labor_total_usd?.toLocaleString() || 0} · Materials ${est.materials_total_usd?.toLocaleString() || 0} · {est.timeline || "—"} · conf {est.confidence}
                </div>
                {est.owner_review_reason ? (
                  <div className="text-[10.5px] text-red-300 mt-1">⚠ {est.owner_review_reason}</div>
                ) : null}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function CyclesTab({ cycles }) {
  if (cycles.length === 0) {
    return <div className="text-center py-10 text-zinc-500">No cycles recorded yet.</div>;
  }
  return (
    <div className="grid gap-2">
      {cycles.map((c) => (
        <Card key={c.id} className="bg-black border-zinc-800">
          <CardContent className="p-3 text-[12px]">
            <div className="flex items-center justify-between text-[11px] text-zinc-400">
              <span>
                {relTime(c.started_at)} · {c.duration_ms}ms · {c.total_output} outputs · {c.bh_open ? "BH open" : "BH closed"}
              </span>
              <span>{c.errored_count ? `${c.errored_count} err` : ""} {c.under_quota_count ? `${c.under_quota_count} under` : ""}</span>
            </div>
            <div className="mt-2 grid grid-cols-3 sm:grid-cols-6 gap-1">
              {(c.agents || []).map((a) => {
                const tone = STATUS_TONES[a.status] || STATUS_TONES.asleep;
                return (
                  <div key={a.slug} className={`rounded px-2 py-1 border ${tone.border}`}>
                    <div className="flex items-center gap-1.5">
                      {a.persona_initials ? (
                        <span
                          className="inline-flex items-center justify-center h-3.5 w-3.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 text-[8px] font-semibold leading-none"
                          title={a.persona_name || ""}
                        >
                          {a.persona_initials}
                        </span>
                      ) : null}
                      <div className="text-[10px] text-zinc-300 truncate">{a.slug}</div>
                    </div>
                    <div className={`text-[11px] font-semibold ${tone.text}`}>
                      {a.output_count} · {a.status}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function SwarmSettingsDialog({ open, onOpenChange, settings, onSaved }) {
  const [draft, setDraft] = useState(settings || null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(settings || null);
  }, [settings, open]);

  if (!draft) return null;

  function patch(k, v) {
    setDraft((d) => ({ ...d, [k]: v }));
  }

  function patchAgent(slug, v) {
    setDraft((d) => ({
      ...d,
      enabled_agents: { ...(d.enabled_agents || {}), [slug]: !!v },
    }));
  }

  async function save() {
    setSaving(true);
    try {
      const patchBody = {
        enabled: draft.enabled,
        cycle_minutes: Number(draft.cycle_minutes) || 180,
        timezone: draft.timezone,
        open_hour: Number(draft.open_hour) || 8,
        close_hour: Number(draft.close_hour) || 19,
        open_days: draft.open_days || [0, 1, 2, 3, 4, 5],
        enabled_agents: draft.enabled_agents || {},
      };
      const updated = await crmApi.swarmSettings(patchBody);
      toast.success("Swarm settings saved");
      onSaved?.(updated);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark max-w-[680px] bg-black border-zinc-800 text-white overflow-y-auto max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <SettingsIcon className="h-4 w-4" />
            Swarm Settings
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Cadence, business-hours window, and per-agent enable toggles.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Switch
              checked={!!draft.enabled}
              onCheckedChange={(v) => patch("enabled", !!v)}
              id="swarm-enabled"
              data-testid="swarm-enabled-toggle"
            />
            <Label htmlFor="swarm-enabled" className="text-white">
              Swarm enabled
            </Label>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1 block">
                Cycle minutes
              </Label>
              <Input
                type="number"
                min={15}
                max={1440}
                value={draft.cycle_minutes ?? 180}
                onChange={(e) => patch("cycle_minutes", e.target.value)}
                className="bg-zinc-950 border-zinc-800 text-white"
                data-testid="swarm-cycle-minutes"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1 block">
                Open hour (BH)
              </Label>
              <Input
                type="number"
                min={0}
                max={23}
                value={draft.open_hour ?? 8}
                onChange={(e) => patch("open_hour", e.target.value)}
                className="bg-zinc-950 border-zinc-800 text-white"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1 block">
                Close hour (BH)
              </Label>
              <Input
                type="number"
                min={1}
                max={24}
                value={draft.close_hour ?? 19}
                onChange={(e) => patch("close_hour", e.target.value)}
                className="bg-zinc-950 border-zinc-800 text-white"
              />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1 block">
              Per-agent toggles
            </Label>
            <div className="grid sm:grid-cols-2 gap-2">
              {Object.entries(draft.enabled_agents || {}).map(([slug, on]) => (
                <div
                  key={slug}
                  className="flex items-center justify-between rounded border border-zinc-800 bg-zinc-950 px-3 py-2"
                >
                  <span className="text-[12.5px] text-zinc-200">{slug}</span>
                  <Switch
                    checked={!!on}
                    onCheckedChange={(v) => patchAgent(slug, !!v)}
                    data-testid={`swarm-agent-toggle-${slug}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="border-zinc-800 text-white hover:bg-zinc-900"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-400 text-black font-semibold"
            data-testid="swarm-settings-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SwarmConsole() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyAgent, setBusyAgent] = useState("");
  const [busyAll, setBusyAll] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tab, setTab] = useState("content");
  const [content, setContent] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [consultProgress, setConsultProgress] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [s, c, ld, a, e, cy, ct] = await Promise.all([
        crmApi.swarmStatus(),
        crmApi.swarmContent({ limit: 60 }),
        crmApi.swarmLeadDrafts({ limit: 50 }),
        crmApi.swarmAudiences({ limit: 100 }),
        crmApi.swarmEstimates({ limit: 30 }),
        crmApi.swarmCycles({ limit: 12 }),
        crmApi.calendarToday().catch(() => null),
      ]);
      setStatus(s);
      setContent(c?.items || []);
      setDrafts(ld?.items || []);
      setAudiences(a?.items || []);
      setEstimates(e?.items || []);
      setCycles(cy?.cycles || []);
      setConsultProgress(ct || null);
    } catch (err) {
      toast.error(err.message || "Failed to load swarm state");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
    const id = setInterval(loadAll, 30000); // 30s auto-refresh
    return () => clearInterval(id);
  }, [loadAll]);

  async function runOne(slug) {
    setBusyAgent(slug);
    try {
      await crmApi.swarmRun({ agent_slugs: [slug] });
      toast.success(`${slug} cycle dispatched`);
      await loadAll();
    } catch (err) {
      toast.error(err.message || "Force run failed");
    } finally {
      setBusyAgent("");
    }
  }

  async function runAll() {
    setBusyAll(true);
    try {
      await crmApi.swarmRun();
      toast.success("Full cycle dispatched");
      await loadAll();
    } catch (err) {
      toast.error(err.message || "Cycle run failed");
    } finally {
      setBusyAll(false);
    }
  }

  async function updateContent(id, status) {
    try {
      await crmApi.swarmUpdateContent(id, { status });
      await loadAll();
    } catch (err) {
      toast.error(err.message || "Update failed");
    }
  }

  async function updateDraft(id, patch) {
    try {
      await crmApi.swarmUpdateLeadDraft(id, patch);
      await loadAll();
    } catch (err) {
      toast.error(err.message || "Update failed");
    }
  }

  async function updateAudience(id, patch) {
    try {
      await crmApi.swarmUpdateAudience(id, patch);
      await loadAll();
    } catch (err) {
      toast.error(err.message || "Update failed");
    }
  }

  const last = status?.last_cycle;
  const lastByAgent = useMemo(() => {
    const out = {};
    for (const a of (last?.agents || [])) out[a.slug] = a;
    return out;
  }, [last]);

  // Dana CAN-SPAM gate stats — surface across the last few cycles so the
  // operator sees compliance health at a glance even before the next cycle.
  const danaStats = useMemo(() => {
    const recent = (cycles || []).slice(0, 8);
    let passed = 0;
    let blocked = 0;
    let warnings = 0;
    let cyclesWithData = 0;
    let lastSeen = null;
    for (const c of recent) {
      const a = (c.agents || []).find((x) => x.slug === "lead_outreach");
      const d = a?.details;
      if (!d) continue;
      passed += d.dana_passed || 0;
      blocked += d.dana_blocked || 0;
      warnings += d.dana_warnings || 0;
      cyclesWithData += 1;
      if (!lastSeen) lastSeen = c.started_at;
    }
    return { passed, blocked, warnings, cyclesWithData, lastSeen };
  }, [cycles]);

  const kpis = status?.kpis || {};
  const bh = status?.business_hours;
  const settings = status?.settings;

  return (
    <div className="space-y-6" data-testid="owner-swarm-console">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-white">
            <Activity className="h-5 w-5 text-amber-400" />
            Black Ridge Swarm
          </h1>
          <p className="text-sm text-zinc-400">
            12 runners across 9 doer personas — 8 backend on 24/7, 4 frontend during business hours.{" "}
            {bh ? (
              <span className={bh.is_open_now ? "text-emerald-300" : "text-zinc-500"}>
                {bh.summary} ({bh.is_open_now ? "OPEN" : "CLOSED"})
              </span>
            ) : null}
          </p>
          <div
            className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px]"
            data-testid="swarm-identity-banner"
          >
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-200">
              <Compass className="h-3 w-3" />
              Orchestrated by
              <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-100 text-[8.5px] font-semibold">PH</span>
              <span className="font-medium">Pat Harper</span>
            </span>
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-violet-500/10 border border-violet-500/30 text-violet-200">
              <ShieldAlert className="h-3 w-3" />
              Compliance gate
              <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-violet-500/20 border border-violet-500/40 text-violet-100 text-[8.5px] font-semibold">DW</span>
              <span className="font-medium">Dana Wills</span>
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setSettingsOpen(true)}
            className="border-zinc-800 text-white hover:bg-zinc-900"
            data-testid="swarm-open-settings"
          >
            <SettingsIcon className="h-3.5 w-3.5 mr-1.5" />
            Settings
          </Button>
          <Button
            size="sm"
            onClick={runAll}
            disabled={busyAll}
            className="bg-amber-500 hover:bg-amber-400 text-black font-semibold"
            data-testid="swarm-run-now"
          >
            {busyAll ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Zap className="h-3.5 w-3.5 mr-1.5" />}
            Run cycle now
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={loadAll}
            className="border-zinc-800 text-white hover:bg-zinc-900"
            data-testid="swarm-refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {loading ? (
        <Card className="bg-zinc-950 border-zinc-800">
          <CardContent className="p-10 flex items-center gap-2 text-zinc-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading swarm state…
          </CardContent>
        </Card>
      ) : null}

      {/* KPI tiles */}
      {status ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-8 gap-3">
          <Kpi
            icon={CalendarCheck}
            label="Consults today"
            value={
              consultProgress
                ? `${(consultProgress.scheduled ?? 0) + (consultProgress.completed ?? 0)} / ${consultProgress.target_min}-${consultProgress.target_max}`
                : "—"
            }
            hint={
              consultProgress
                ? consultProgress.on_track
                  ? "on track today"
                  : `goal: ${consultProgress.target_min}-${consultProgress.target_max}/day`
                : "loading…"
            }
            tone={consultProgress?.on_track ? "pass" : "warn"}
            testId="swarm-kpi-consults-today"
          />
          <Kpi
            icon={Activity}
            label="Cycles run"
            value={settings?.cycle_count ?? 0}
            hint={settings?.last_cycle_at ? `last ${relTime(settings.last_cycle_at)}` : "—"}
          />
          <Kpi
            icon={Clock}
            label="Next cycle"
            value={settings?.next_cycle_at ? relTime(settings.next_cycle_at) : "—"}
            hint={`every ${settings?.cycle_minutes ?? 180}m`}
          />
          <Kpi icon={Megaphone} label="Content ready" value={kpis.content_ready ?? 0} hint={`${kpis.content_total ?? 0} total`} tone="pass" />
          <Kpi icon={Target} label="Audiences" value={kpis.audiences_total ?? 0} hint="groups identified" />
          <Kpi
            icon={Brain}
            label="Drafts ready"
            value={kpis.drafts_ready_total ?? 0}
            hint={`T1=${kpis.drafts_ready_by_tier?.[1] ?? 0} T2=${kpis.drafts_ready_by_tier?.[2] ?? 0} T3=${kpis.drafts_ready_by_tier?.[3] ?? 0}`}
            tone={kpis.drafts_ready_total > 0 ? "pass" : "default"}
          />
          <Kpi
            icon={ShieldCheck}
            label="Estimates"
            value={kpis.estimates_total ?? 0}
            hint={`${kpis.estimates_owner_review ?? 0} need review`}
            tone={kpis.estimates_owner_review > 0 ? "warn" : "default"}
          />
          <Kpi
            icon={ShieldAlert}
            label="Dana gate"
            value={
              danaStats.cyclesWithData > 0
                ? `${danaStats.passed} / ${danaStats.passed + danaStats.blocked}`
                : "—"
            }
            hint={
              danaStats.cyclesWithData > 0
                ? `${danaStats.blocked} blocked · ${danaStats.warnings} warn (${danaStats.cyclesWithData}c)`
                : "no auto-send activity yet"
            }
            tone={
              danaStats.blocked > 0
                ? "warn"
                : danaStats.passed > 0
                ? "pass"
                : "default"
            }
            testId="swarm-kpi-dana"
          />
        </div>
      ) : null}

      {/* Agent status grid */}
      {status ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(status.agents || []).map((spec) => (
            <AgentCard
              key={spec.slug}
              spec={spec}
              lastRecord={lastByAgent[spec.slug]}
              busy={busyAgent === spec.slug || busyAll}
              onForceRun={runOne}
            />
          ))}
        </div>
      ) : null}

      {/* Detail tabs */}
      {status ? (
        <Tabs value={tab} onValueChange={setTab} className="space-y-3">
          <TabsList className="bg-zinc-950 border border-zinc-800">
            <TabsTrigger value="content" data-testid="swarm-tab-content">
              Content Bank ({content.length})
            </TabsTrigger>
            <TabsTrigger value="drafts" data-testid="swarm-tab-drafts">
              Lead Drafts ({drafts.length})
            </TabsTrigger>
            <TabsTrigger value="audiences" data-testid="swarm-tab-audiences">
              Audiences ({audiences.length})
            </TabsTrigger>
            <TabsTrigger value="estimates" data-testid="swarm-tab-estimates">
              Estimates ({estimates.length})
            </TabsTrigger>
            <TabsTrigger value="cycles" data-testid="swarm-tab-cycles">
              Cycles ({cycles.length})
            </TabsTrigger>
          </TabsList>
          <TabsContent value="content">
            <ContentBankTab items={content} onMark={updateContent} />
          </TabsContent>
          <TabsContent value="drafts">
            <LeadDraftsTab items={drafts} onUpdate={updateDraft} />
          </TabsContent>
          <TabsContent value="audiences">
            <AudienceTab items={audiences} onUpdate={updateAudience} />
          </TabsContent>
          <TabsContent value="estimates">
            <EstimatesTab items={estimates} />
          </TabsContent>
          <TabsContent value="cycles">
            <CyclesTab cycles={cycles} />
          </TabsContent>
        </Tabs>
      ) : null}

      <SwarmSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        settings={status?.settings}
        onSaved={loadAll}
      />
    </div>
  );
}
